import { useEffect, useState } from 'react';
import { useQuery } from 'data-fetching-client';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { CardWrapper, CardSection, CardHeader } from 'foundations-components/transitional/components/Card';
import { Select } from 'foundations-components/transitional/components/Select';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Link } from 'foundations-components/transitional/components/Link';
import NoData from 'reporting-ui-components/components/shared/NoData';
import Visualization from 'reporting-visualizations/Visualization';
import { ReportVisualizationErrorBoundary } from './ReportVisualizationErrorBoundary';
import { VIDEO_RETENTION_QUERY_FIELD_NAME, VIDEO_VIEWS_QUERY_FIELD_NAME
// @ts-expect-error untyped module
} from 'video-reports-lib/constants';
import { VIDEO_RETENTION_QUERY, getRetentionVisualization
// @ts-expect-error untyped module
} from 'video-reports-lib/query/videoRetention';
import { VIDEO_VIEWS_TIME_SERIES_QUERY, getViewsTimeSeriesVisualization
// @ts-expect-error untyped module
} from 'video-reports-lib/query/videoViews';
import { buildVideoObjectCoordinate, toDateInt
// @ts-expect-error untyped module
} from 'video-reports-lib/utils/unifiedEvents';
import { getVideoDetailsUrl
// @ts-expect-error untyped module
} from 'video-reports-lib/utils/url';
import { useReportingFiltersProvider } from '../../providers/ReportingFiltersProvider';
import { useVideos } from '../../hooks/useVideos';
import { useContentDetailsDataProvider } from '../../providers/ContentDetailsDataProvider';
import { contentIsPublished } from 'content-management-data-lib';
import { getContentPropertyValue } from 'content-management-data-lib';
import { CONTENT_PLATFORM_PROPERTY_NAME } from 'content-management-data-lib';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const VideoViewsChart = ({
  contentId,
  dateRange,
  frequency,
  videos
}) => {
  const videoObjectCoordinates = videos.map(video => buildVideoObjectCoordinate(video.value));
  const reportQuery = useQuery(VIDEO_VIEWS_TIME_SERIES_QUERY, {
    variables: {
      contentId,
      endDateInt: toDateInt(dateRange.endDate || ''),
      frequency: frequency.toLowerCase()[0],
      startDateInt: toDateInt(dateRange.startDate || ''),
      videoObjectCoordinates
    },
    skip: videos.length === 0
  });
  let cardContent;
  if (reportQuery.loading) {
    cardContent = /*#__PURE__*/_jsx(LoadingSpinner, {
      layout: "centered",
      minHeight: 200
    });
  } else if (reportQuery.data) {
    // TODO: Make more specific when video queries are typed
    const reportData = reportQuery.data[VIDEO_VIEWS_QUERY_FIELD_NAME];
    if (!reportData || reportData.isEmpty && reportData.isEmpty()) {
      cardContent = /*#__PURE__*/_jsx(NoData, {
        "data-test-id": "video-views-chart-no-data"
      });
    } else {
      const visualization = getViewsTimeSeriesVisualization(reportData, {
        dateRange,
        frequency
      });
      cardContent = /*#__PURE__*/_jsx(Visualization, {
        visualization: visualization
      });
    }
  } else {
    cardContent = /*#__PURE__*/_jsx(NoData, {
      "data-test-id": "video-views-chart-no-data"
    });
  }
  return /*#__PURE__*/_jsxs(CardWrapper, {
    compact: true,
    children: [/*#__PURE__*/_jsx(CardHeader, {
      title: I18n.text('content-management-details-lib.reporting.videoViewsChart.header')
    }), /*#__PURE__*/_jsx(CardSection, {
      children: /*#__PURE__*/_jsx(ReportVisualizationErrorBoundary, {
        children: cardContent
      })
    })]
  });
};
const VideoRetentionChart = ({
  contentId,
  dateRange,
  videos
}) => {
  const [selectedVideoId, setSelectedVideoId] = useState(videos.length > 0 ? videos[0].value : '');
  const reportQuery = useQuery(VIDEO_RETENTION_QUERY, {
    variables: {
      contentId,
      endDateInt: toDateInt(dateRange.endDate || ''),
      startDateInt: toDateInt(dateRange.startDate || ''),
      videoObjectCoordinate: buildVideoObjectCoordinate(selectedVideoId)
    },
    skip: !selectedVideoId
  });
  const videoOptions = videos.map(video => ({
    value: video.value,
    text: video.text
  }));
  const selectedVideo = videos.find(v => v.value === selectedVideoId);
  useEffect(() => {
    if (!videos.map(v => v.value).includes(selectedVideoId) && videos.length > 0) {
      setSelectedVideoId(videos[0].value);
    }
  }, [videos, selectedVideoId]);
  let cardContent;
  if (reportQuery.loading) {
    cardContent = /*#__PURE__*/_jsx(LoadingSpinner, {
      layout: "centered",
      minHeight: 200
    });
  } else if (reportQuery.data) {
    // TODO: Make more specific when video queries are typed
    const reportData = reportQuery.data[VIDEO_RETENTION_QUERY_FIELD_NAME];
    if (!reportData || reportData.isEmpty && reportData.isEmpty()) {
      cardContent = /*#__PURE__*/_jsx(NoData, {
        "data-test-id": "video-retention-chart-no-data"
      });
    } else {
      const duration = selectedVideo ? selectedVideo.duration : null;
      const visualization = getRetentionVisualization(reportData, {
        duration
      });
      cardContent = /*#__PURE__*/_jsx(Visualization, {
        visualization: visualization
      });
    }
  } else {
    cardContent = /*#__PURE__*/_jsx(NoData, {
      "data-test-id": "video-retention-chart-no-data"
    });
  }
  const toolbar = videos.length > 0 && /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "content-management-details-lib.reporting.videoViewsChart.videoSelectorLabel"
    }), /*#__PURE__*/_jsx(Select, {
      buttonUse: "transparent",
      onChange: e => {
        setSelectedVideoId(e.target.value);
      },
      options: videoOptions,
      value: selectedVideoId
    }), selectedVideo && /*#__PURE__*/_jsx(Link, {
      href: getVideoDetailsUrl(selectedVideo.value, {
        source: 'content-management-details-lib'
      }),
      external: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "content-management-details-lib.reporting.videoRetentionChart.viewDetails"
      })
    })]
  });
  return /*#__PURE__*/_jsxs(CardWrapper, {
    compact: true,
    children: [/*#__PURE__*/_jsx(CardHeader, {
      title: I18n.text('content-management-details-lib.reporting.videoRetentionChart.header'),
      toolbar: toolbar
    }), /*#__PURE__*/_jsx(CardSection, {
      children: /*#__PURE__*/_jsx(ReportVisualizationErrorBoundary, {
        children: cardContent
      })
    })]
  });
};
const VideoPerformanceCharts = ({
  contentId,
  contentPublishDate
}) => {
  const {
    videos
  } = useVideos(contentId);
  const {
    dateRange,
    frequency
  } = useReportingFiltersProvider();
  if (videos.length === 0) {
    return null;
  }

  // Adjust date range if it starts before content publish date
  let adjustedDateRange = dateRange;
  if (dateRange.startDate) {
    const dateRangeStart = I18n.moment(dateRange.startDate);
    const contentPublishMoment = I18n.moment.utc(contentPublishDate);
    if (dateRangeStart.isSameOrBefore(contentPublishMoment)) {
      adjustedDateRange = Object.assign({}, dateRange, {
        startDate: contentPublishMoment.format('YYYY-MM-DD')
      });
    }
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(VideoViewsChart, {
      contentId: contentId,
      dateRange: adjustedDateRange,
      frequency: frequency,
      videos: videos
    }), /*#__PURE__*/_jsx(VideoRetentionChart, {
      contentId: contentId,
      dateRange: adjustedDateRange,
      videos: videos
    })]
  });
};
const VideoPerformance = ({
  contentId
}) => {
  const {
    contentObject
  } = useContentDetailsDataProvider();
  if (!contentId) {
    return null;
  }
  const contentStateIsPublished = contentIsPublished(contentObject);
  const contentPublishDate = getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.PUBLISH_DATE);
  if (!contentStateIsPublished && contentPublishDate) {
    return null;
  }
  return /*#__PURE__*/_jsx(VideoPerformanceCharts, {
    contentPublishDate: contentPublishDate,
    contentId: contentId
  });
};
export default VideoPerformance;